import {configureStore} from '@reduxjs/toolkit'
import {size} from 'es-toolkit/compat'

import {createSentryReduxEnhancer} from '../services/logging'

import authReducer from './features/auth'
import coreReducer, {initialDataReceived} from './features/core'
import modalReducer from './features/modal'
import orderReducer, {setOrders} from './features/order'
import toastReducer from './features/toast'

export const makeStore = () => {
  const sentryReduxEnhancer = createSentryReduxEnhancer({
    stateTransformer: ({core, order}: AppState) => ({
      // core
      ...core,
      colors: size(core.colors),
      countries: size(core.countries),
      finishGroups: size(core.finishGroups),
      materialConfigs: size(core.materialConfigs),
      materials: size(core.materials),
      materialGroups: size(core.materialGroups),
      printingMethods: size(core.printingMethods),
      provider: core.provider?.id,
      // order
      orders: size(order.orders),
      orderStatusFilterValues: order.statusFilter.serialize().filterValues,
    }),
    actionTransformer: action => {
      if (initialDataReceived.match(action)) {
        return {
          type: action.type,
          payload: {
            // Only show lengths of newly fetched initial core data to minimize Sentry breadcrumb size
            colors: size(action.payload.colors),
            countries: size(action.payload.countries),
            finishGroups: size(action.payload.finishGroups),
            materialConfigs: size(action.payload.materialConfigs),
            materials: size(action.payload.materials),
            materialGroups: size(action.payload.materialGroups),
            printingMethods: size(action.payload.printingMethods),
            provider: action.payload.provider?.id,
          },
        }
      }

      if (setOrders.match(action)) {
        return {
          type: action.type,
          // Only track orderNumber from received orders to minimize Sentry breadcrumb size
          payload: {
            ...action.payload,
            orders: action.payload.orders.map(order => order.orderNumber),
          },
        }
      }
      return action
    },
  })

  return configureStore({
    devTools: process.env.NODE_ENV !== 'production',
    reducer: {
      auth: authReducer,
      core: coreReducer,
      modal: modalReducer,
      order: orderReducer,
      toast: toastReducer,
    },
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        serializableCheck: {
          // Action payloads are allowed to be non-serializable
          ignoreActions: true,
          // State must contain serializable values, except for the paths below
          ignoredPaths: ['modal.modalConfig.contentProps', 'auth.user', 'order.statusFilter'],
        },
      }),
    enhancers: getDefaultEnhancers => {
      return getDefaultEnhancers().concat(sentryReduxEnhancer)
    },
  })
}

export type AppStore = ReturnType<typeof makeStore>
export type AppState = ReturnType<AppStore['getState']>
export type AppDispatch = AppStore['dispatch']
